import { EventName } from "module/analytics/types"
import { AppNetwork } from "module/network/container/AppNetwork"
import { formatNumberPositionSize, formatNumberUSD } from "util/format"
import { formatTimeStamp } from "util/time"

import { Badge, Box, HStack, Tbody, Td, Text, Th, Thead, Tr } from "@chakra-ui/react"
import { PositionHistory, PositionSide } from "@perp/sdk-curie"
import { MarketListIcon } from "component/MarketListIcon"
import { PerpLink } from "component/PerpLink"
import { PerpTable } from "component/PerpTable"
import { useTranslation } from "react-i18next"
import { PositionLiquidationHistoryContainer } from "sdk-react/history/PositionLiquidationHistoryContainer"

interface TradeHistoryTableRowProps {
    positionHistory: PositionHistory
}

const PositionBadge = ({ isLong }: { isLong: boolean }) => {
    const { t } = useTranslation()

    return (
        <Badge variant="subtle" colorScheme={isLong ? "green" : "red"}>
            {isLong ? t("position.side_long") : t("position.side_short")}
        </Badge>
    )
}

export const TradeHistoryTableRow = ({ positionHistory }: TradeHistoryTableRowProps) => {
    const { txId, timestamp, baseSymbol, side, size, price, tradingFee, isClosed } = positionHistory
    const { explorerBuilder } = AppNetwork.useContainer()
    const link = explorerBuilder(txId, "transaction")

    return (
        <Tr>
            <Td textDecoration="underline">
                <PerpLink trackingProps={{ eventName: EventName.LINK_TRADER_HISTORY_TABLE_ROW_CLICK }} href={link}>
                    {formatTimeStamp(timestamp)}
                </PerpLink>
            </Td>
            <Td>{formatNumberPositionSize(size)}</Td>
            <Td>
                <HStack>
                    <Box boxSize="16px">
                        <MarketListIcon coin={baseSymbol} boxSize="16px" />
                    </Box>
                    <Text>{baseSymbol}</Text>
                    <PositionBadge isLong={side === PositionSide.LONG} />
                </HStack>
            </Td>
            {/*TODO: update these code with utils */}
            <Td>
                {price.toFixed(3)}
                {isClosed ? (
                    <>
                        <br />
                        {"(Closed Price)"}
                    </>
                ) : null}
            </Td>
            <Td>{formatNumberUSD({ num: positionHistory.realizedPnl })}</Td>
            <Td>{formatNumberUSD({ num: tradingFee })}</Td>
        </Tr>
    )
}

export const PositionLiquidationTable = () => {
    const { positionHistories, fetchNextPage, isLoading } = PositionLiquidationHistoryContainer.useContainer()
    const { t } = useTranslation()

    return (
        <PerpTable onLoadMore={fetchNextPage} isLoadingMore={isLoading}>
            <Thead>
                <Tr>
                    <Th>{t("general.time")}</Th>
                    <Th>{t("position.position_size")}</Th>
                    <Th>{t("position.side")}</Th>
                    <Th>{t("general.price")}</Th>
                    <Th>{t("general.realized_profit_loss")}</Th>
                    <Th>{t("general.transaction_fee")}</Th>
                </Tr>
            </Thead>
            <Tbody>
                {positionHistories.map(history => {
                    return <TradeHistoryTableRow positionHistory={history} key={history.txId} />
                })}
            </Tbody>
        </PerpTable>
    )
}
